import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { ElectionsApi } from '@/services/political/electionsApi'
import type { Election } from '../../types/models'

/** Stands in for "no election" — a Radix select item needs a non-empty value. */
const NONE = '__none__'

/** Shared key, so every election picker reads one cached list. */
export const ELECTIONS_QUERY_KEY = ['elections']

/** An election is still current for a few days after polling day. */
export function isRecent(election: Election): boolean {
  if (!election.electionDate) return true
  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() - 3)
  return new Date(election.electionDate) >= cutoff
}

export interface ElectionSelectProps {
  /** Election id, or '' when the line item is not tied to one. */
  value: string
  onChange: (electionId: string) => void
  /**
   * How many topics the advertiser can book against each election, keyed by
   * election id. Shown next to each option so the picker says up front which
   * elections the advertiser actually has topics for.
   */
  topicCounts?: Record<string, number>
  /**
   * Offer only these elections — those the advertiser can book, through its
   * linked campaigns or its political budgets. The current value is always
   * listed regardless.
   */
  electionIds?: string[]
  /** Name to show for `value` while the election list is still loading. */
  fallbackName?: string
  /**
   * Keep elections whose polling day has passed out of the list — nothing new
   * can be booked against them. On by default; the current value is always
   * listed regardless, and the hidden ones stay one click away.
   */
  hideHeldElections?: boolean
  /** Marks the picker as failing validation. */
  invalid?: boolean
  disabled?: boolean
  className?: string
}

/**
 * Picks the election, or other political activity, a political line item
 * advertises for — dated elections first, undated political purposes after.
 */
export function ElectionSelect({
  value,
  onChange,
  topicCounts,
  electionIds,
  fallbackName,
  hideHeldElections = true,
  invalid,
  disabled,
  className,
}: ElectionSelectProps) {
  const { t } = useTranslation()
  const [showHeld, setShowHeld] = useState(false)

  const { data: elections = [], isLoading } = useQuery({
    queryKey: ELECTIONS_QUERY_KEY,
    queryFn: ElectionsApi.getAll,
    staleTime: 5 * 60 * 1000,
  })

  function label(election: Election): string {
    const parts = [election.name || t('elections.unnamed')]
    if (election.electionDate) parts.push(`(${election.electionDate})`)
    if (!isRecent(election)) parts.push(`[${t('elections.alreadyHeld')}]`)
    if (election.testOnly) parts.push(`[${t('elections.test')}]`)
    return parts.join(' ')
  }

  const { dated, undated } = useMemo(() => {
    const offered = electionIds
      ? elections.filter((e) => electionIds.includes(e.id) || e.id === value)
      : elections
    const sorted = [...offered].sort(
      (a, b) =>
        a.electionDate.localeCompare(b.electionDate) ||
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    )
    return {
      dated: sorted.filter((e) => !!e.electionDate),
      undated: sorted.filter((e) => !e.electionDate),
    }
  }, [elections, electionIds, value])

  const selected = elections.find((e) => e.id === value)

  // How many past elections the filter is keeping back. The current value is
  // never one of them, so an existing line item still shows what it booked.
  const heldCount = dated.filter((e) => !isRecent(e) && e.id !== value).length
  const hidePast = hideHeldElections && !showHeld
  const datedShown = hidePast ? dated.filter((e) => isRecent(e) || e.id === value) : dated

  function renderItem(election: Election) {
    const count = topicCounts?.[election.id]
    return (
      <SelectItem key={election.id} value={election.id}>
        <span className="flex w-full min-w-0 items-center gap-2">
          <span className="truncate">{label(election)}</span>
          {count !== undefined && (
            <span className="ml-auto shrink-0 text-xs text-(--ad-text-muted)">
              {t('political.topicCount', { count })}
            </span>
          )}
        </span>
      </SelectItem>
    )
  }

  return (
    <div className="flex min-w-0 flex-col items-start gap-1.5">
      <Select
        value={value}
        disabled={disabled}
        onValueChange={(v) => onChange(v === NONE ? '' : (v ?? ''))}
      >
        <SelectTrigger aria-invalid={invalid || undefined} className={cn('w-full', className)}>
          <SelectValue placeholder={t('political.electionPlaceholder')}>
            {selected ? label(selected) : value ? (fallbackName || value) : null}
          </SelectValue>
        </SelectTrigger>
        {/* Grouped here rather than through the root's `label`, so the two
            kinds of political activity stay apart. */}
        <SelectContent loading={isLoading}>
          <SelectItem value={NONE}>
            <span className="text-muted-foreground">{t('political.noElection')}</span>
          </SelectItem>
          {datedShown.length > 0 && (
            <SelectGroup>
              <SelectLabel>{t('nav.elections')}</SelectLabel>
              {datedShown.map(renderItem)}
            </SelectGroup>
          )}
          {undated.length > 0 && (
            <SelectGroup>
              <SelectLabel>{t('political.generalPurpose')}</SelectLabel>
              {undated.map(renderItem)}
            </SelectGroup>
          )}
        </SelectContent>
      </Select>

      {/* The filter only earns a control when it is actually holding some back. */}
      {hideHeldElections && heldCount > 0 && (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs font-normal text-(--ad-text-muted) hover:text-(--ad-text)"
          onClick={() => setShowHeld((shown) => !shown)}
        >
          {showHeld ? t('elections.hidePast') : t('elections.showPast', { count: heldCount })}
        </Button>
      )}
    </div>
  )
}
